import {
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useId, useRef, useState} from 'react';
import {ReactComponent as AddIcon} from '../../../../src/assests/svg/add.svg';
import {ReactComponent as ExpandIcon} from '../../../assests/svg/down-triangle.svg';
import AurButton from '../aur-button/AurButton';
import styles from './style.module.scss';
import {ISelectItem} from './types';

const ITEM_HEIGHT = 60; // Single item/option height
const ITEMS_WITHOUT_SCROLL = 6; // Default items/options panel height

interface MuiCustodianSelectProps {
  value?: ISelectItem | null;
  options: ISelectItem[];
  placeholder?: string;
  isDisabled?: boolean;
  ariaLabel?: string;
  dataTestId?: string;
  onChange: (option: ISelectItem | null) => void;
  onAddCustodian?: () => void;
  submitButtonText?: string;
  isMultiRow?: boolean;
  handleMultiRow?: () => void;
  isReadOnly: boolean;
  itemsCountwithNoScroll?: number; // Minimum items/option shows with no scrollbar appear
  addButtonLabel?: string;
  hintText?: string;
  onOpen?: () => void;
}

const AurCustomSingleSelect = ({
  value,
  options,
  placeholder, //'Pick Custodian'
  isDisabled = false,
  ariaLabel,
  dataTestId,
  onChange,
  onAddCustodian,
  submitButtonText = t('common.apply'),
  isMultiRow = false,
  handleMultiRow,
  isReadOnly,
  itemsCountwithNoScroll = ITEMS_WITHOUT_SCROLL,
  addButtonLabel,
  hintText,
  onOpen,
}: MuiCustodianSelectProps) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLLIElement | null>(null);
  const menuListRef = useRef<HTMLUListElement | null>(null);
  const hintId = useId();

  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ISelectItem | null>(
    value ?? null
  );

  useEffect(() => {
    setSelectedItem(value ?? null);
  }, [value]);

  useEffect(() => {
    if (open) {
      firstItemRef.current?.focus();
    }
  }, [open]);

  const handleToggle = () => {
    if (isReadOnly || isDisabled) return;

    if (isMultiRow) {
      handleMultiRow?.();
      return;
    }

    setOpen((prev) => {
      if (!prev) {
        onOpen?.();
      }
      return !prev;
    });
    setTimeout(() => {
      menuListRef.current?.focus();
    }, 0);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (option: ISelectItem) => {
    setSelectedItem(option);
  };

  const handleApply = () => {
    onChange(selectedItem);
    setOpen(false);
  };

  const displayValue = value?.label ?? placeholder;

  const handleKeyDownOfListItem = (event: React.KeyboardEvent) => {
    if (event.key !== 'Escape') event.stopPropagation();
  };

  return (
    <Box className={`aur-custom-select ${isDisabled ? 'disabled' : ''}`}>
      <span id={hintId} style={{display: 'none'}}>
        {hintText ?? t('common.dropdown.optionSelectHintsText')}
      </span>
      {/* Anchor */}
      <Box
        ref={anchorRef}
        tabIndex={0}
        aria-label={ariaLabel ?? placeholder}
        data-testid={dataTestId}
        onClick={handleToggle}
        role="button"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${dataTestId}-listbox`}
        aria-describedby={hintId}
        className={`select-button ${isReadOnly ? 'default-cursor' : ''} ${open ? 'open-state' : ''} ${isDisabled ? 'disabled-cursor' : ''} `}
      >
        <Typography
          className={`typo-text body3_size ${isReadOnly ? 'display-text' : 'primary-text'}`}
        >
          <span tabIndex={0}>{displayValue}</span>
        </Typography>

        {(isDisabled || !isReadOnly) && (
          <Box
            tabIndex={0}
            className={`expand-icon-container ${open ? 'pressed-state' : ''}`}
          >
            <ExpandIcon
              className={`expand-icon ${open ? 'open' : ''} ${isDisabled ? 'disabled-expand-icon' : ''}`}
            />
          </Box>
        )}
      </Box>

      {/* Dropdown */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        className="custom-popper custom-popper-single-select"
        transition
        tabIndex={0}
        modifiers={[
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top-end', 'right-start', 'left-start'],
            },
          },
        ]}
      >
        {({TransitionProps}) => (
          <Grow {...TransitionProps}>
            <Paper className="custom-paper">
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* List */}
                  <MenuList
                    ref={menuListRef}
                    tabIndex={0}
                    id={`${dataTestId}-listbox`}
                    role="listbox"
                    sx={{
                      // These styles are dynamic and based on items/options data
                      maxHeight: `${ITEM_HEIGHT * itemsCountwithNoScroll}px`,
                      overflowY:
                        options.length > itemsCountwithNoScroll
                          ? 'auto'
                          : 'hidden',
                    }}
                    className="mui-list pick-custodian-list"
                    autoFocus={false}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape' && open) {
                        e.preventDefault();
                        setOpen(false);
                      }
                    }}
                  >
                    {/* Add Custodian */}
                    {onAddCustodian && [
                      <MenuItem
                        tabIndex={0}
                        key="add-custodian-menu-item"
                        ref={firstItemRef}
                        className="add-menu-item"
                        onClick={() => {
                          onAddCustodian();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onAddCustodian();
                          }
                        }}
                      >
                        <AddIcon className="add-icon" />
                        <Typography
                          component="span"
                          className="typo-text body2_size item-text"
                        >
                          {addButtonLabel}
                        </Typography>
                      </MenuItem>,
                      <Divider
                        key="add-custodian-divider"
                        className={`${styles.aurMultiSelectPanelDivider} divider`}
                      />,
                    ]}

                    {options.map((option) => (
                      <MenuItem
                        tabIndex={0}
                        role="option"
                        id={`${dataTestId}-option-${option.id}`}
                        aria-selected={selectedItem?.id === option.id}
                        key={option.id}
                        selected={selectedItem?.id === option.id}
                        onClick={() => handleSelect(option)}
                      >
                        <Typography
                          component="span"
                          className="typo-text body2_size item-text"
                        >
                          {option.label}
                        </Typography>
                      </MenuItem>
                    ))}
                  </MenuList>

                  {/* Sticky Apply */}
                  <Divider
                    className={`${styles.aurMultiSelectPanelDivider} divider`}
                  />
                  <Box className="apply-button-container">
                    <AurButton
                      type="continue"
                      tabIndex={0}
                      title={submitButtonText}
                      label={submitButtonText}
                      size="Medium"
                      disabled={!selectedItem}
                      onClick={() => handleApply()}
                      dataTestId="Apply-button"
                      onKeyDown={handleKeyDownOfListItem}
                    />
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default AurCustomSingleSelect;
